import { observer } from "mobx-react-lite";
import React, { useContext, useEffect, useRef, useState } from "react";

import { UserContext } from "../contexts";
import useWindowSize from "../hooks/useWindowSize";
import { Upgrade,UpgradeCategory } from "../types";
import { scrollToWithOffset } from "../util";
import Carousel from "./Carousel";
import CollapsableImageContent from "./CollapsableImageContent";
import FullscreenModal from "./FullscreenModal";
import InclusionDetail from "./InclusionDetail";
import Tabs from "./Tabs";

/**
 * Tabbed content shown on the Upgrade and Lifestyle options screens (they are the exact same)
 * @constructor
 */
const UpgradeOptionsTabs = observer(
  (props: {
    categories: UpgradeCategory[];
    upgrades: Upgrade[];
    selectedUpgrades: Map<number, Upgrade> | Map<number, Upgrade[]>;
    setSelectedUpgrade: (categoryId: number, upgrade: Upgrade | null) => void;
    tabIndex?: number; // Optionally control tab index from parent component
    onChangeTabIndex?: (index: number) => void;
    slideIndexes?: number[]; // Optionally slide indexes from parent component
    onChangeSlideIndex?: (tabIndex: number, slideIndex: number) => void;
  }) => {
    const userContext = useContext(UserContext);
    const [currentTabIndex, setCurrentTabIndex] = useState<number>(0);
    const [currentSlideIndex, setCurrentSlideIndex] = useState<number[]>([]);
    const [compareTableHeight, setCompareTableHeight] = useState<number>(0);

    const [isModalVisible, setIsModalVisible] = useState(false);
    const [isModalClosing, setIsModalClosing] = useState(false);
    const [modalUpgrade, setModalUpgrade] = useState<Upgrade | null>(null);

    const windowSize = useWindowSize();

    const columnRefs = useRef(new Map());

    useEffect(() => {
      const slideIndexes = [];
      for (let i = 0; i < props.categories.length; i++) {
        slideIndexes.push(0);
      }
      setCurrentSlideIndex(slideIndexes);
    }, []);

    useEffect(() => {
      if (props.tabIndex !== undefined && props.tabIndex !== currentTabIndex) {
        setCurrentTabIndex(props.tabIndex);
      }
    }, [props.tabIndex]);

    useEffect(() => {
      updateCompareTableHeight();
    }, [windowSize, props.tabIndex]);

    useEffect(() => {
      if (props.slideIndexes !== undefined) {
        setCurrentSlideIndex(props.slideIndexes);
      }
    }, [props.slideIndexes]);

    const updateCompareTableHeight = () => {
      const h = $(".compare-table-body").outerHeight();
      if(h !== undefined){
        setCompareTableHeight(h + 10);
      }
    };

    const onClickMoreDetails = (upgrade: Upgrade) => {
      setModalUpgrade(upgrade);
      setIsModalVisible(true);
    };

    const onModalClose = () => {
      setIsModalVisible(false);
      setIsModalClosing(false);
    };

    const onChangeTabIndex = (index: number) => {
      setCurrentTabIndex(index);
      if (props.onChangeTabIndex) {
        props.onChangeTabIndex(index);
      }
    };

    const onClickUpgrade = (item: Upgrade) => {
      const existing = props.selectedUpgrades.get(item.category_id);

      if (Array.isArray(existing)) {
        // The parent component will take care of toggling when it is an array (Lifestyle Options)
        props.setSelectedUpgrade(item.category_id, item);
      } else {
        if (existing && existing.id == item.id) {
          props.setSelectedUpgrade(item.category_id, null);
        } else {
          props.setSelectedUpgrade(item.category_id, item);
        }
      }

      const node = columnRefs.current.get(item.id);
      if (node) {
        node.scrollIntoView({
          behavior: "smooth",
          inline: "start",  // Options: 'start', 'center', 'end', 'nearest'
          block: "nearest",
        });
      }
      scrollToWithOffset(".compare-table", -40);
    };

    const getUpgradeImageOverride = (upgrade: Upgrade) => {
      // Selecting an interior style can override the upgrade image shown
      let image = upgrade.image;
      if (upgrade.image_overrides && userContext.selectedInteriorStyle) {
        if (
          Object.keys(upgrade.image_overrides).indexOf(
            userContext.selectedInteriorStyle.id.toString()
          ) > -1
        ) {
          image = upgrade.image_overrides[userContext.selectedInteriorStyle.id];
        }
      }
      return image;
    };

    const renderTabContent = (tabIndex: number) => {
      const items = props.upgrades.filter((item) => {
        return item.category_id == props.categories[tabIndex].id;
      });

      function isSelected(item: Upgrade) {
        const existing = props.selectedUpgrades.get(item.category_id);
        if (Array.isArray(existing)) {
          return existing.filter((_item) => _item.id === item.id).length > 0;
        } else {
          return !!existing && existing.id == item.id;
        }
      }

      return (
        <>
          <div className="options">
            {items.map((item) => {
              return (
                <CollapsableImageContent
                  key={item.id}
                  layout={"upgrade"}
                  image={getUpgradeImageOverride(item)}
                  title={item.title}
                  price={item.price}
                  promo_price={item.promo_price}
                  innerHtml={item.content}
                  selected={isSelected(item)}
                  insetTick={true}
                  onClick={() => onClickUpgrade(item)}
                  showDetailsAsAccordion={false}
                  details={item}
                  fullscreenButton={true}
                />
              );
            })}
          </div>
          <div className="compare-table">
            <div className="compare-table-header">
              <h2>Compare Options</h2>
              <div className="asterisk">* Design Specific</div>
            </div>
            <div className="compare-table-overflow">
              <div className="compare-table-overflow-inner">
                <div className="compare-table-body">
                  <div className="column-headings">
                    <div className="column-heading mobile">
                      <span>Option</span>
                    </div>
                    {items.map((item, i) => {
                      return (
                        <div 
                          key={i} 
                          className={`column-heading ${isSelected(item) ? "selected" : ""}`}
                          ref={(node) => {
                            if (node) {
                              columnRefs.current.set(item.id, node);
                            } else {
                              columnRefs.current.delete(item.id);
                            }
                          }}
                        >
                          <span>{ item.title.split(" ")[1] }</span>
                          <div className="background" style={{height: `${compareTableHeight}px`}}></div>
                          <div className="outline" onClick={() => onClickUpgrade(item)} style={{height: `${compareTableHeight}px`}}></div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="columns">
                    {items[0].compare_table.map((row, index) => {
                      return (
                        <div key={index} className="column-row">
                          <div className="row-heading">{ row.title }</div>
                          {items.map((item, i) => {
                            return (
                              <div key={i} className={`column ${item.compare_table[index].optional ? "optional" : ""}`}>
                                {(item.compare_table[index] && item.compare_table[index].items.length) ? (
                                  <ul>
                                    {item.compare_table[index].items.map((listitem, j) => {
                                      return (
                                        <li key={j}>{listitem}</li>
                                      );
                                    })}
                                  </ul>
                                ) : (
                                  <span className="empty">-</span>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      );
    };

    return (
      <>
        <Tabs
          headerClassName={"container"}
          titles={props.categories.map((item) => item.title)}
          renderContent={renderTabContent}
          tabIndex={currentTabIndex}
          onChangeTabIndex={(index) => onChangeTabIndex(index)}
        />

        {isModalVisible && modalUpgrade !== null && (
          <FullscreenModal
            title={modalUpgrade.title}
            closing={isModalClosing}
            onClose={onModalClose}
          >
            <InclusionDetail
              inclusion={modalUpgrade}
              onClickBack={() => setIsModalClosing(true)}
              hideBlurb={true}
            />
          </FullscreenModal>
        )}
      </>
    );
  }
);

export default UpgradeOptionsTabs;
